import polylabel from "polylabel"

import { useContext, useEffect, useState } from "react";
import { actionSetErrorMessage, AppMessagesContext, Localization } from "@viamap/viamap2-common";
import { actionAddDataLayer, actionSetFeatureLayerVisibility, actionSetMapInteractionState, actionSetMapInteractionStateDefault, actionSetShowUserLocation, MapInteractionState, MapitStateContext } from "src/states/MapitState";
import { LayerFunc } from "src/managers/LayerFunc";
import { ProtectedFeature } from "./ProtectedFeature";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { PropertyInfoInterface } from "src/managers/PropertyInfoInterface";
import { DataCollector, DC } from "src/dataCollector/DataCollector";
import { MitLatLng } from "src/managers/MapFacade";
import { SearchBarGeneral } from "./SearchBarGeneral";
import { AddressAutocomplete, CVRNumberAutocomplete, CVRNavnAutocomplete, CVRPersonAutocomplete, EjerlavAutocomplete, EjerNavnAutocomplete, MatrikelAutocomplete, VejAutocomplete, FeatureLayerAutoComplete, StednavnAutoComplete, BFELookup } from "src/managers/SearchBarGeneralPlugin";
import { PropertyInformation } from "src/managers/PropertyInformation";
import { MapFacadeMaplibre } from "src/managers/MapFacadeMaplibre";
import { MapInteractionStateInfoBox } from "./MapInteractionStateInfoBox";
import { BacktoMapit3Banner } from "./BacktoMapit3Banner";
import { toggleWindow, useWindowContext } from "src/WindowManager/useWindowContext";
import { closeWindow, openWindow, sendParams, WindowId, WindowState } from "src/WindowManager/WindowState";
import { MitWebsocket, ServerMessages } from "src/common/managers/MitWebsocket";
import { UnlockedLayerInfoBox } from "./UnlockedLayerInfoBox";
import { parse as WKTparse } from "wkt";
import { GenerateGeomUtils } from "src/managers/GenerateGeomUtils";
import { message } from "./Message"
import { BroadCastReceiverBar } from "./BroadCastReceiverBar";
import { actionStepOut, RightPaneContext, RightPaneModus } from "src/states/RightPaneState";
import { OnMapButton } from "./GlassButtons";
import { ActionTracker } from "src/common/managers/ActionTracker";
import { MIBIcon } from "src/componentsUtils/MIBIcon";

export function NavigationBarMicro(
  props: { onCollapse: () => void }
) {

  const { dispatch: appMessageDispatch } = useContext(AppMessagesContext);
  const { state: mapitState, dispatch: mapitStateDispatch } = useContext(MapitStateContext);
  const { hasAccessToFeature } = useContext(ApplicationStateContext);

  const { isActive, dispatch: rightPaneDispatch } = useContext(RightPaneContext)
  const { dispatch: windowDispatch } = useWindowContext();


  // ------------------------------- HANDLERS --------------------------------------
  const tegneModeActive = isActive(RightPaneModus.Map_Measure);

  async function drawMatrikelLocation(matrNr: string, ejerlavskode: number, betegnelse: string): Promise<void> {
    let res = await PropertyInfoInterface.getGeojsonOfCadasters([{ matrikelNr: matrNr, ejerlavKode: ejerlavskode }])

    if (res.features[0]) {
      let [x, y] = polylabel(res.features[0].geometry.coordinates[0], 0.00001)
      let layerInfo = LayerFunc.createLayerInfoForOnePoint(new MitLatLng(y, x), betegnelse + " " + matrNr, { ...res.features[0].properties });

      windowDispatch(openWindow(WindowId.ShowSearchField));

      if (hasAccessToFeature(Feature.ThemeNewSec)) {
        layerInfo = LayerFunc.createLayerInfoForOnePoint(new MitLatLng(y, x), betegnelse + " " + matrNr, {});
      }
      mapitStateDispatch(actionAddDataLayer(layerInfo, true, true));
      let bfeData = await DC.WFS.Utils.bfeUnderPoint([x, y]);
      windowDispatch(sendParams(WindowId.PropertyInformationDialog, { bfeNr: bfeData?.sfeNr, ejerlavskode: bfeData?.ejerl, matrnr: bfeData?.matnr }));
    } else {
      appMessageDispatch(actionSetErrorMessage(Localization.getText("No data for given cadaster")));
    }
  }

  /* ------------------------------------------------------- */
  return (
    <>
      <div className="NavigationTopBar small" id="Mit-NavigationTopBar" style={{ zIndex: 1, pointerEvents: "none" }}>
        <OnMapButton className="Collapse" title={Localization.getText("Open/Close Sidebar")} title-placement="right" onClick={() => props.onCollapse()} >
          <MIBIcon icon="leftRightBar" />
        </OnMapButton>

        <ProtectedFeature feature={Feature.DeviceLocation} contentsIfNoAccess={<></>} >
          <OnMapButton className={(mapitState.showUserLocation ? "ActiveImg" : "")}
            title={Localization.getText("Your Location")}
            onClick={() => {
              mapitStateDispatch(actionSetShowUserLocation(!mapitState.showUserLocation));
            }}
          >
            <MIBIcon icon="UserLocation" variant={mapitState.showUserLocation ? "Bold" : undefined} />
          </OnMapButton>
        </ProtectedFeature>
        {!tegneModeActive ? <>
          <ProtectedFeature feature={Feature.BasicMap} contentsIfNoAccess={<></>} >
            <OnMapButton className={(mapitState.mapInteractionState == MapInteractionState.ClickToSetMarker ? "ActiveImg" : "")}
              title={Localization.getText("Add Pin")}
              onClick={() => {
                if (MapInteractionState.ClickToSetMarker !== mapitState.mapInteractionState) {
                  mapitStateDispatch(actionSetMapInteractionState(MapInteractionState.ClickToSetMarker))
                } else {
                  mapitStateDispatch(actionSetMapInteractionStateDefault())
                }
              }}
            >
              <MIBIcon icon="point" />
            </OnMapButton>
          </ProtectedFeature>
          <ProtectedFeature feature={Feature.SearchBar} contentsIfNoAccess={<></>}>
            <SearchBarGeneral
              showWindow={true}
              autocompleters={[
                new BFELookup(async (data: {
                  BFEnummer: number,
                  SFEnummer?: string | number, // finde center of sfe, 
                  type: "SFE" | "BPFG" | "Ejerlejlighed",
                  geometri?: string, //WKT string
                }) => {
                  ActionTracker.track("NavBarMain", "Search", "BFE");
                  let layerName = data.BFEnummer + ` (${data.type === "Ejerlejlighed" ? Localization.getText("Condominium") : data.type})`;
                  if (data.geometri) {
                    let tmp = WKTparse(data.geometri)
                    if (tmp?.coordinates[0]) {
                      let coords = GenerateGeomUtils.convertFromUTMtoLatLng({ x: tmp.coordinates[0], y: tmp.coordinates[1] })
                      let layerInfo = LayerFunc.createLayerInfoForOnePoint(new MitLatLng(coords.lat, coords.lng), layerName, { ...data });
                      mapitStateDispatch(actionAddDataLayer(layerInfo, true));
                      return;
                    }
                  }
                  if ("geometri" in data) {
                    delete data.geometri;
                  }
                  if (data.type) {
                    let EBR = await DataCollector.DF_GQL.EBR.EBRAdresseOgPunkt([data.BFEnummer.toString()]); if (EBR[0]?.geometri) {
                      let tmp = WKTparse(EBR[0]?.geometri)
                      if (tmp?.coordinates[0]) {
                        let coords = GenerateGeomUtils.convertFromUTMtoLatLng({ x: tmp.coordinates[0], y: tmp.coordinates[1] })
                        let layerInfo = LayerFunc.createLayerInfoForOnePoint(new MitLatLng(coords.lat, coords.lng), layerName, { ...data });
                        mapitStateDispatch(actionAddDataLayer(layerInfo, true));
                        return;
                      }
                    }
                  }
                  if (data.type == "SFE") {
                    let geoJson = await PropertyInfoInterface.getGeojsonOfSFE(data.BFEnummer);
                    let coords = polylabel(geoJson.features[0].geometry.coordinates, 0.00001)
                    let layerInfo = LayerFunc.createLayerInfoForOnePoint(new MitLatLng(coords[1], coords[0]), layerName, { ...geoJson.features[0].properties });
                    mapitStateDispatch(actionAddDataLayer(layerInfo, true));
                    return;
                  }
                  message.info(Localization.getText("No Position found for BFE"));
                }), // If selected sets context limiting other searches
                new EjerlavAutocomplete(() => ActionTracker.track("NavBarMain", "Search", "Ejerlav")), // If selected sets context limiting other searches
                new VejAutocomplete(() => ActionTracker.track("NavBarMain", "Search", "Vej")), // If selected sets context limiting other searches
                new MatrikelAutocomplete((data: any) => {
                  ActionTracker.track("NavBarMain", "Search", "Matrikel");
                  drawMatrikelLocation(data.matrikelnummer, data.ejerlavskode, data.visningstekst); // NB Async
                }),

                new AddressAutocomplete(async (data: any) => {
                  ActionTracker.track("NavBarMain", "Search", "Adresse");
                  let createPoint = true;
                  // convert coordinates from UTM32 to WGS84
                  let wgsCoords = data.geometri.coordinates[0].toReversed();
                  if (createPoint) {
                    let layerInfo = LayerFunc.createLayerInfoForOneAddressV2(data.vejnavn, data.husnummer, wgsCoords.reverse());
                    mapitStateDispatch(actionAddDataLayer(layerInfo, true));
                  } else {
                    windowDispatch(sendParams(WindowId.PropertyInformationDialog, { bfeNr: await DC.WFS.Utils.bfeUnderPoint([wgsCoords[1], wgsCoords[0]]) }));
                  }


                }),
                new CVRNumberAutocomplete((data: any) => {
                  ActionTracker.track("NavBarMain", "Search", "CVRNummer");
                  // Open CompanyInfo for cvr = data.cvrNummer
                  windowDispatch(openWindow(WindowId.CompanyInformationDialog, { cvrNr: data.cvrNummer }));
                }),
                new CVRNavnAutocomplete((data: any) => {
                  // Open CompanyInfo for cvr = data.cvrNummer
                  ActionTracker.track("NavBarMain", "Search", "CVRNavn");
                  windowDispatch(openWindow(WindowId.CompanyInformationDialog, { cvrNr: data.cvrNummer }));
                }),
                new CVRPersonAutocomplete((data: any) => {
                  ActionTracker.track("NavBarMain", "Search", "CVRPerson");
                  // Open PersonInfo for navn og cvr person enhedsnummer
                  let gaeldendeNavn = data.navne.find((n: any) => { return n.periode.gyldigTil === null });
                  let adr = data.deltagerpersonMetadata?.nyesteBeliggenhedsadresse;
                  let postnr = adr?.postnummer;
                  let adresse = adr?.vejnavn + " " + adr?.husnummerFra; // Todo: bogstav og side...
                  windowDispatch(openWindow(WindowId.PersonInformationDialog, {
                    navn: gaeldendeNavn.navn,
                    postnr: postnr,
                    adresse: adresse,
                    vejnavn: adr?.vejnavn,
                    husnr: adr?.husnummerFra,
                    foedselsdato: undefined,
                    cvrPersonId: data.enhedsNummer
                  }));
                }),
                new EjerNavnAutocomplete((data: any, searchText?: string) => {
                  ActionTracker.track("NavBarMain", "Search", "EjerNavn");
                  // If the property is owned by more than one person. Make sure we show information for the person we searched for.
                  let owners = data?.property_record?.hits?.hits?.[0]?._source?.ejf_ejere_liste
                    || data?.property_record?.hits?.hits?.[0]?._source.ejf_ejere.split(",").filter((a) => a).map((a) => ({ navn: a.trim() })) || []

                  let selectedOwnerData = PropertyInformation.propertyOwnerMatchingName(owners, searchText || "");
                  windowDispatch(openWindow(WindowId.PersonInformationDialog, {
                    navn: selectedOwnerData.navn,
                    postnr: selectedOwnerData.postnr,
                    foedselsdato: selectedOwnerData.foedselsdato && new Date(selectedOwnerData.foedselsdato),
                    adresse: selectedOwnerData.adresse
                  }));
                }),
                new StednavnAutoComplete((data: any) => {
                  ActionTracker.track("NavBarMain", "Search", "Stednavn");
                  (mapitState.map as MapFacadeMaplibre).zoomToData(data.bbox, 150)
                }),
                // new BFEAutocomplete((data:any)=> {
                //   // todo: Open PropertyInfo for bfe = data.bfe_nr
                //   alert("not implemented yet");
                // }),
                new FeatureLayerAutoComplete(Object.values(mapitState.featureLayers), mapitState.selectedFeatureLayerKeys, (selected) => {
                  ActionTracker.track("NavBarMain", "Search", "FeatureLayer");
                  const show = !mapitState.selectedFeatureLayerKeys.includes(selected.layer.label)
                  const title = selected.layer.translationTable?.[Localization.getLanguage()]?.[selected.layer.label] || selected.layer.label
                  mapitStateDispatch(actionSetFeatureLayerVisibility(title, show, [{ key: selected.layer.label, layer: selected }], true));
                })
              ]}
              callbackOnSelection={(type: string, props: any) => { alert(`got callback Type:${type} props: ${JSON.stringify(props)}`) }}
            />
            <ProtectedFeature feature={Feature.BroadcastReceiver} contentsIfNoAccess={<></>} >
              <BroadCastReceiverBar />
            </ProtectedFeature>


          </ProtectedFeature>
        </>
          :
          <div className="OnMapButton" title={Localization.getText("Leave drawing mode")} onClick={() => { rightPaneDispatch(actionStepOut()) }} style={{ aspectRatio: "unset", paddingLeft: "1em", textWrap: "nowrap", backgroundColor: "rgba(255, 236, 212, 1)" }} >
            {Localization.getText("Drawing mode active")}
            <MIBIcon icon="dismiss" />
          </div>
        }
        <div className="NavRightFlex" >
          <MapInteractionStateInfoBox />
          <UnlockedLayerInfoBox />
          <BacktoMapit3Banner />
        </div>
      </div>
      <div className="TopRightOnMapButtons">
        <ProtectedFeature feature={Feature.WebsocketGlobalChat} contentsIfNoAccess={<></>} >
          <NotificationButton />
        </ProtectedFeature>
        <MapDescriptionButton />
        {/* <DownloadManagerButton /> */}
      </div>

    </>
  )
}


function DownloadManagerButton() {
  const { state: windowState, dispatch: windowDispatch } = useWindowContext();

  return (
    <OnMapButton
      title={Localization.getText("Downloads")}
      title-placement="left"
      onClick={() => {
        if (windowState.find((a) => a.id === WindowId.DownloadManager)?.state === WindowState.Open) {
          windowDispatch(closeWindow(WindowId.DownloadManager));
        } else {
          windowDispatch(openWindow(WindowId.DownloadManager));
        }
      }}
    >
      <></>
      {/* <ImportCurve /> */}
    </OnMapButton>
  )

}

function NotificationButton() {
  const { state: windowState, dispatch: windowDispatch } = useWindowContext();
  const [Notifications, setNotifications] = useState(0);
  const [connections, setConnections] = useState(0);

  useEffect(() => {
    const onMessage = (data: ServerMessages) => {
      if (data.type === "members") {
        setConnections(data.action.members.length || 0);
      }
    };
    MitWebsocket.subscribe("ping", onMessage);
    return () => {
      MitWebsocket.unsubscribe("ping", onMessage);
    }

  }, []);

  useEffect(() => {
    let onMessage = () => {
      setNotifications((old) => old + 1);
    };

    MitWebsocket.subscribe("chat", onMessage);
    return () => {
      MitWebsocket.unsubscribe("chat", onMessage);
    }
  }, [windowState]);


  return (
    <OnMapButton className={"OnMapButton" + (Notifications ? " circle" : "") + (connections ? " square" : "")} data-users={connections} data-notis={Notifications > 99 ? "!+" : Notifications} style={{ marginLeft: "auto" }}
      title={"Chat"}
      title-placement="left"
      onClick={() => {

        if (windowState.find((a) => a.id === WindowId.ChatWindow)?.state === WindowState.Open) {
          windowDispatch(closeWindow(WindowId.ChatWindow));
        } else {
          windowDispatch(openWindow(WindowId.ChatWindow));
          setNotifications(0);
        }
      }}
    >
      <MIBIcon icon="notification" />
    </OnMapButton>
  )
}

export function MapDescriptionButton() {
  const { state: windowState, dispatch: windowDispatch } = useWindowContext();
  const { state: mapitState } = useContext(MapitStateContext);
  const { hasAccessToFeature } = useContext(ApplicationStateContext);

  if (!(mapitState.mapInfo.mapDescription || hasAccessToFeature(Feature.EditMapDescription))) {
    return <></>
  }

  return (
    <OnMapButton className={(mapitState.mapInfo.mapDescription ? " square" : "")}
      title={Localization.getText("Map Description")} title-placement="left" onClick={() => {

        windowDispatch(toggleWindow(windowState, WindowId.MapDescription));
      }} >
      <MIBIcon icon="mapDescription" />
    </OnMapButton>
  )
}